import React, { useState, useEffect, useRef } from "react";
import logo from "../assets/logo.png";
import { Link, useNavigate } from "react-router-dom";
import { IoSearch, IoMenu, IoClose } from "react-icons/io5";
import { LuUser } from "react-icons/lu";
import { BsHandbag } from "react-icons/bs";
import gsap from "gsap";
import { useSelector } from "react-redux";
import { BiLogIn } from "react-icons/bi";
import { LoginModal, RegisterModal, ForgotPasswordModal } from "./Modals";
import { useAuth } from "../contexts/AuthContext";
import api from "../utilis/api";

const Navbar = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const navRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);
  const searchInputRef = useRef(null);
  const resultsRef = useRef(null);
  const { isLoggedIn } = useSelector((state) => state.user);
  const { quantity } = useSelector((state) => state.cart);
  const { isLoginModalOpen, activeTab, setActiveTab, openLoginModal, closeLoginModal } = useAuth();

  // Search functionality
  const searchProducts = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    setIsSearching(true);
    try {
      const response = await api.get(`/products/search?q=${encodeURIComponent(query)}`);
      setSearchResults(response.data.products || []);
      setShowResults(true);
    } catch (error) {
      console.error("Search error:", error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Handle search input change with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery) {
        searchProducts(searchQuery);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Handle search form submission
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/fragrance?search=${encodeURIComponent(searchQuery)}`);
      setIsSearchExpanded(false);
      setSearchQuery("");
      setShowResults(false);
    }
  };

  // Handle product selection from search results
  const handleProductSelect = (productId) => {
    navigate(`/product/${productId}`);
    setIsSearchExpanded(false);
    setSearchQuery("");
    setShowResults(false);
  };

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (resultsRef.current && !resultsRef.current.contains(event.target) &&
          searchInputRef.current && !searchInputRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Common navigation links from footer
  const navLinks = [
    { title: "Women's Fragrances", link: "/fragrance?category=women" },
    { title: "Men's Fragrances", link: "/fragrance?category=men" },
    { title: "Best Sellers", link: "/fragrance?tags=Best%20Sales,Top%20Sellers,bestseller" },
    { title: "Gift Sets", link: "/fragrance?category=gifts" },
    { title: "New Year Sale", link: "/fragrance?category=new_year_sale" },
    { title: "About Us", link: "/about" },
  ];

  // Search expand/collapse animation
  const toggleSearch = () => {
    if (!isSearchExpanded) {
      setIsSearchExpanded(true);
      // Use setTimeout to ensure the element is rendered before animating
      setTimeout(() => {
        if (searchRef.current) {
          gsap.fromTo(
            searchRef.current,
            { width: 0, opacity: 0 },
            { width: 300, opacity: 1, duration: 0.4, ease: "power2.out" }
          );
        }
      }, 10);
    } else {
      if (searchRef.current) {
        gsap.to(searchRef.current, {
          width: 0,
          opacity: 0,
          duration: 0.3,
          ease: "power2.in",
          onComplete: () => setIsSearchExpanded(false),
        });
      }
    }
  };

  // Entrance animation
  useEffect(() => {
    gsap.from(navRef.current, {
      y: -80,
      opacity: 0,
      duration: 1,
      ease: "power3.out",
    });
  }, []);

  const barRef = useRef(null);
  const modalRef = useRef(null);
  const overlayRef = useRef(null);

  /* ── Modal open animation ── */
  useEffect(() => {
    if (!isLoginModalOpen) return;
    gsap.set(overlayRef.current, { opacity: 0 });
    gsap.set(modalRef.current, { scale: 0.85, opacity: 0, y: 20 });
    gsap.to(overlayRef.current, { opacity: 1, duration: 0.3 });
    gsap.to(modalRef.current, {
      scale: 1,
      opacity: 1,
      y: 0,
      duration: 0.45,
      ease: "back.out(1.6)",
    });
  }, [isLoginModalOpen]);

  const closeModal = () => {
    gsap.to(overlayRef.current, { opacity: 0, duration: 0.25 });
    gsap.to(modalRef.current, {
      scale: 0.85,
      opacity: 0,
      y: 16,
      duration: 0.3,
      onComplete: () => closeLoginModal(),
    });
  };

  return (
    <>
      <nav ref={navRef} className="relative py-4 px-4 md:px-10 font-outfit">
        <div className="max-w-[1440px] mx-auto flex justify-between items-center gap-4">
          {/* 1. LOGO SECTION */}
          <div className="flex-shrink-0">
            <Link to="/">
              <img
                src={logo}
                alt="Gift of God Fragrances"
                className="h-[80px] md:h-[90px] w-auto object-contain"
              />
            </Link>
          </div>

          {/* 2. CENTER NAVIGATION LINKS (Desktop) */}
          <div className="hidden lg:flex flex-1 justify-center">
            <div className="flex items-center gap-8">
              {navLinks.map((link, index) => (
                <Link
                  key={index}
                  to={link.link}
                  className="text-white hover:text-[#f0dc82] transition-colors duration-200 text-sm font-medium whitespace-nowrap"
                >
                  {link.title}
                </Link>
              ))}
            </div>
          </div>

          {/* 3. RIGHT SECTION - Search + Icons */}
          <div className="flex items-center gap-4 md:gap-6 text-white">
            {/* Collapsible Search Bar (Desktop) */}
            <div className="hidden md:flex items-center relative">
              {isSearchExpanded && (
                <div className="relative">
                  <form onSubmit={handleSearchSubmit}>
                    <div
                      ref={searchRef}
                      className="relative flex items-center overflow-hidden"
                      style={{ width: 0 }}
                    >
                      <input
                        ref={searchInputRef}
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search fragrances..."
                        className="w-full h-10 px-4 pr-12 rounded-full text-gray-700 bg-white focus:outline-none text-sm"
                        autoFocus
                      />
                      <button
                        type="button"
                        onClick={toggleSearch}
                        className="absolute right-1 h-8 w-8 flex items-center justify-center rounded-full bg-gradient-to-b from-[#e5cf94] to-[#b38f36] text-black hover:opacity-90 transition-opacity"
                      >
                        <IoClose size={16} />
                      </button>
                    </div>
                  </form>
                  
                  {/* Search Results Dropdown */}
                  {showResults && (
                    <div
                      ref={resultsRef}
                      className="absolute top-full left-0 w-full mt-2 bg-white rounded-lg shadow-lg border border-gray-200 max-h-96 overflow-y-auto z-50"
                      style={{ minWidth: "300px" }}
                    >
                      {isSearching ? (
                        <div className="p-4 text-center text-gray-500">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#b38f36] mx-auto"></div>
                          <p className="mt-2 text-sm">Searching...</p>
                        </div>
                      ) : searchResults.length > 0 ? (
                        <>
                          <div className="p-3 border-b border-gray-100">
                            <p className="text-sm text-gray-600 font-medium">
                              {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} found
                            </p>
                          </div>
                          {searchResults.slice(0, 8).map((product) => (
                            <div
                              key={product._id}
                              onClick={() => handleProductSelect(product._id)}
                              className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-50 last:border-b-0 flex items-center gap-3"
                            >
                              {product.images && product.images[0] && (
                                <img
                                  src={product.images[0]}
                                  alt={product.name}
                                  className="w-12 h-12 object-cover rounded-lg flex-shrink-0"
                                />
                              )}
                              <div className="flex-1 min-w-0">
                                <h4 className="text-sm font-medium text-gray-900 truncate">
                                  {product.name}
                                </h4>
                                <p className="text-xs text-gray-500 truncate">
                                  {product.brand} • {product.category}
                                </p>
                                <p className="text-sm font-semibold text-[#b38f36]">
                                  ${product.price}
                                </p>
                              </div>
                            </div>
                          ))}
                          {searchResults.length > 8 && (
                            <div className="p-3 border-t border-gray-100">
                              <button
                                onClick={() => handleSearchSubmit({ preventDefault: () => {} })}
                                className="w-full text-center text-sm text-[#b38f36] hover:text-[#8b6b2a] font-medium"
                              >
                                View all {searchResults.length} results
                              </button>
                            </div>
                          )}
                        </>
                      ) : searchQuery.trim() ? (
                        <div className="p-4 text-center text-gray-500">
                          <IoSearch size={24} className="mx-auto mb-2 text-gray-300" />
                          <p className="text-sm">No products found for "{searchQuery}"</p>
                          <p className="text-xs text-gray-400 mt-1">Try different keywords</p>
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              )}
              {!isSearchExpanded && (
                <button
                  onClick={toggleSearch}
                  className="hover:text-[#f0dc82] transition-colors p-2"
                >
                  <IoSearch size={24} />
                </button>
              )}
            </div>

            {/* Cart Icon with Badge */}
            <div className="relative group cursor-pointer">
              <BsHandbag
                size={26}
                onClick={() => navigate("/cart")}
                className="hover:text-[#f0dc82] transition-colors"
              />
              <span className="absolute -top-1 -right-2 bg-gradient-to-b from-[#f0dc82] to-[#b38f36] text-black text-[10px] font-bold w-5 h-5 flex items-center justify-center rounded-full border border-white">
                {quantity !== 0 ? quantity : 0}
              </span>
            </div>

            {/* User Icon */}
            {isLoggedIn ? (
              <Link
                to="/user"
                className="hover:text-[#f0dc82] transition-colors"
              >
                <LuUser size={28} />
              </Link>
            ) : (
              <Link
                onClick={() => openLoginModal()}
                className="hover:text-[#f0dc82] transition-colors"
              >
                <BiLogIn size={28} />
              </Link>
            )}

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden text-white text-3xl"
            >
              {isOpen ? <IoClose /> : <IoMenu />}
            </button>
          </div>
        </div>

        {/* MOBILE SEARCH BAR (Visible only on small screens) */}
        <div className="md:hidden mt-4 px-2">
          <form onSubmit={handleSearchSubmit}>
            <div className="relative w-full flex items-center">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search fragrances..."
                className="w-full h-10 px-4 rounded-full text-sm bg-white focus:outline-none"
              />
              <button 
                type="submit"
                className="absolute right-1 h-8 w-12 flex items-center justify-center rounded-full bg-gradient-to-b from-[#e5cf94] to-[#b38f36] text-black"
              >
                <IoSearch size={18} />
              </button>
            </div>
          </form>
        </div>

        {/* MOBILE NAV LINKS MENU */}
        {isOpen && (
          <div
            ref={menuRef}
            className="absolute top-full left-0 w-full bg-black/95 px-6 py-8 flex flex-col gap-6 text-white z-50 shadow-2xl"
          >
            {navLinks.map((link, index) => (
              <Link
                key={index}
                to={link.link}
                className="text-xl hover:text-[#f0dc82] transition-colors"
                onClick={() => setIsOpen(false)}
              >
                {link.title}
              </Link>
            ))}
            <Link to="/contact" className="text-xl hover:text-[#f0dc82] transition-colors" onClick={() => setIsOpen(false)}>
              Contact Us
            </Link>
          </div>
        )}
      </nav>
      {isLoginModalOpen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          {/* Overlay */}
          <div
            ref={overlayRef}
            onClick={closeModal}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          />

          {/* Modal */}
          <div
            ref={modalRef}
            className="relative z-10 bg-white rounded-2xl w-full max-w-md p-8 shadow-2xl"
          >
            {/* Close */}
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-black text-xl"
            >
              ×
            </button>

            {/* Tabs */}
            {activeTab !== "forgot-password" && (
              <div className="flex border-b mb-6">
                {["login", "register"].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`flex-1 py-2 text-sm font-semibold capitalize ${
                      activeTab === tab
                        ? "text-[#b38f36] border-b-2 border-[#b38f36]"
                        : "text-gray-400"
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>
            )}

            {activeTab === "login" && <LoginModal setTab={setActiveTab} onClose={closeModal} />}
            {activeTab === "register" && (
              <RegisterModal setTab={setActiveTab} />
            )}
            {activeTab === "forgot-password" && (
              <ForgotPasswordModal setTab={setActiveTab} />
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;